import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '@/components/ui/table';
import {
  ArrowUpRight,
  ChevronLeft,
  ChevronRight,
  Inbox,
  MessageSquareText,
  Search,
  SlidersHorizontal
} from 'lucide-react';
import { toast } from 'sonner';

interface Feedback {
  id: string;
  value: number;
  comment: string | null;
  stepId: string | null;
  threadId: string | null;
  threadName: string | null;
  userIdentifier: string | null;
  userId: string | null;
  userMetadata: Record<string, unknown>;
  stepOutput: string | null;
  stepInput: string | null;
  createdAt: string | null;
}

type SentimentFilter = 'all' | 'positive' | 'negative';
type CommentFilter = 'all' | 'with' | 'without';
type PeriodFilter = 'all' | '7' | '30' | '90';

const AdminComments = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [feedback, setFeedback] = useState<Feedback[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedFeedback, setSelectedFeedback] = useState<Feedback | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [totalFeedback, setTotalFeedback] = useState(0);
  const [search, setSearch] = useState('');
  const [sentiment, setSentiment] = useState<SentimentFilter>('all');
  const [commentFilter, setCommentFilter] = useState<CommentFilter>('all');
  const [period, setPeriod] = useState<PeriodFilter>('all');
  const [appliedFilters, setAppliedFilters] = useState({
    search: '',
    sentiment: 'all' as SentimentFilter,
    commentFilter: 'all' as CommentFilter,
    period: 'all' as PeriodFilter
  });

  const fetchFeedback = useCallback(async () => {
    try {
      setLoading(true);
      const filters: Record<string, string | number | boolean> = {};
      if (appliedFilters.search) filters.search = appliedFilters.search;
      if (appliedFilters.sentiment !== 'all') {
        filters.value = appliedFilters.sentiment === 'positive' ? 1 : 0;
      }
      if (appliedFilters.commentFilter !== 'all') {
        filters.has_comment = appliedFilters.commentFilter === 'with';
      }
      if (appliedFilters.period !== 'all') {
        const date = new Date();
        date.setDate(date.getDate() - Number(appliedFilters.period));
        filters.date_from = date.toISOString();
      }

      const response = await fetch('/api/feedback', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ page: currentPage, limit: pageSize, filters })
      });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);

      const data = await response.json();
      setFeedback(data.feedback || []);
      setTotalFeedback(data.total || 0);
    } catch (error) {
      console.error('Error fetching feedback:', error);
      toast.error(t('admin.comments.loadError', 'Failed to load feedback'));
    } finally {
      setLoading(false);
    }
  }, [appliedFilters, currentPage, pageSize, t]);

  useEffect(() => {
    fetchFeedback();
  }, [fetchFeedback]);

  const applyFilters = () => {
    setCurrentPage(1);
    setAppliedFilters({ search: search.trim(), sentiment, commentFilter, period });
  };

  const clearFilters = () => {
    setSearch('');
    setSentiment('all');
    setCommentFilter('all');
    setPeriod('all');
    setCurrentPage(1);
    setAppliedFilters({ search: '', sentiment: 'all', commentFilter: 'all', period: 'all' });
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return '—';
    try {
      return new Date(dateString).toLocaleString(
        i18n.language.toLowerCase().startsWith('sr') ? 'sr-RS' : 'en-GB',
        {
          timeZone: 'Europe/Belgrade',
          day: '2-digit',
          month: 'short',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit'
        }
      );
    } catch {
      return dateString;
    }
  };

  const displayText = (text: unknown) => {
    if (!text) return '—';
    if (typeof text === 'string') return text;
    try {
      return JSON.stringify(text, null, 2);
    } catch {
      return String(text);
    }
  };

  const totalPages = Math.max(1, Math.ceil(totalFeedback / pageSize));
  const firstShown = totalFeedback ? (currentPage - 1) * pageSize + 1 : 0;
  const lastShown = Math.min(currentPage * pageSize, totalFeedback);
  const filtersActive = Object.values(appliedFilters).some(
    (value) => value !== 'all' && value !== ''
  );

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-3xl font-bold tracking-tight">
          {t('admin.comments.title', 'Comments')}
        </h1>
        <p className="mt-1 text-sm text-muted-foreground">
          {t('admin.comments.description', 'Review feedback attached to assistant responses.')}
        </p>
      </div>

      <section className="overflow-hidden rounded-xl border border-border bg-card">
        <div className="border-b border-border px-5 py-4">
          <div className="grid gap-3 lg:grid-cols-[minmax(220px,1fr)_170px_170px_150px_auto] lg:items-end">
            <label className="block">
              <span className="mb-1.5 block text-xs font-medium text-muted-foreground">
                {t('admin.comments.filters.search', 'Search')}
              </span>
              <span className="relative block">
                <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                  onKeyDown={(event) => event.key === 'Enter' && applyFilters()}
                  placeholder={t('admin.comments.filters.searchPlaceholder', 'Comment, user or conversation…')}
                  className="h-9 pl-9"
                />
              </span>
            </label>

            <label className="block">
              <span className="mb-1.5 block text-xs font-medium text-muted-foreground">
                {t('admin.comments.filters.sentiment', 'Rating')}
              </span>
              <Select value={sentiment} onValueChange={(value) => setSentiment(value as SentimentFilter)}>
                <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t('admin.comments.filters.allRatings', 'All ratings')}</SelectItem>
                  <SelectItem value="positive">{t('admin.comments.filters.helpful', 'Helpful')}</SelectItem>
                  <SelectItem value="negative">{t('admin.comments.filters.notHelpful', 'Not helpful')}</SelectItem>
                </SelectContent>
              </Select>
            </label>

            <label className="block">
              <span className="mb-1.5 block text-xs font-medium text-muted-foreground">
                {t('admin.comments.filters.comment', 'Comment')}
              </span>
              <Select value={commentFilter} onValueChange={(value) => setCommentFilter(value as CommentFilter)}>
                <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t('admin.comments.filters.allComments', 'All feedback')}</SelectItem>
                  <SelectItem value="with">{t('admin.comments.filters.withComment', 'With comment')}</SelectItem>
                  <SelectItem value="without">{t('admin.comments.filters.withoutComment', 'Without comment')}</SelectItem>
                </SelectContent>
              </Select>
            </label>

            <label className="block">
              <span className="mb-1.5 block text-xs font-medium text-muted-foreground">
                {t('admin.comments.filters.period', 'Period')}
              </span>
              <Select value={period} onValueChange={(value) => setPeriod(value as PeriodFilter)}>
                <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t('admin.comments.filters.allTime', 'All time')}</SelectItem>
                  <SelectItem value="7">{t('admin.comments.filters.last7Days', 'Last 7 days')}</SelectItem>
                  <SelectItem value="30">{t('admin.comments.filters.last30Days', 'Last 30 days')}</SelectItem>
                  <SelectItem value="90">{t('admin.comments.filters.last90Days', 'Last 90 days')}</SelectItem>
                </SelectContent>
              </Select>
            </label>

            <Button onClick={applyFilters} className="h-9">
              <SlidersHorizontal className="mr-2 h-4 w-4" />
              {t('admin.comments.filters.apply', 'Apply')}
            </Button>
          </div>
        </div>

        <div className="flex flex-wrap items-center justify-between gap-3 px-5 py-3">
          <p className="text-sm text-muted-foreground">
            {loading ? <Skeleton className="h-5 w-28" /> : t('admin.comments.results', { count: totalFeedback, defaultValue: `${totalFeedback} feedback entries` })}
          </p>
          {filtersActive ? (
            <Button variant="ghost" size="sm" onClick={clearFilters}>
              {t('admin.comments.filters.clear', 'Clear filters')}
            </Button>
          ) : null}
        </div>

        <div className="overflow-x-auto px-5 pb-2">
          {loading ? (
            <div className="space-y-2 pb-4">
              {Array.from({ length: 5 }).map((_, index) => <Skeleton key={index} className="h-16 w-full" />)}
            </div>
          ) : feedback.length === 0 ? (
            <div className="flex min-h-64 flex-col items-center justify-center text-center">
              <Inbox className="mb-3 h-8 w-8 text-muted-foreground/60" />
              <p className="font-medium">{t('admin.comments.allFeedback.noFeedback', 'No feedback found')}</p>
              <p className="mt-1 text-sm text-muted-foreground">
                {filtersActive
                  ? t('admin.comments.adjustFilters', 'Try changing or clearing the filters.')
                  : t('admin.comments.empty', 'User feedback will appear here.')}
              </p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[130px]">{t('admin.comments.allFeedback.columns.feedback', 'Rating')}</TableHead>
                  <TableHead className="w-[34%]">{t('admin.comments.allFeedback.columns.comment', 'Comment')}</TableHead>
                  <TableHead>{t('admin.comments.allFeedback.columns.user', 'User')}</TableHead>
                  <TableHead>{t('admin.comments.allFeedback.columns.thread', 'Conversation')}</TableHead>
                  <TableHead>{t('admin.comments.allFeedback.columns.date', 'Date')}</TableHead>
                  <TableHead className="text-right">{t('admin.comments.allFeedback.columns.actions', 'Actions')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {feedback.map((item) => (
                  <TableRow key={item.id}>
                    <TableCell className="py-4">
                      <span className={`whitespace-nowrap text-sm font-semibold ${item.value === 1 ? 'text-emerald-700 dark:text-emerald-300' : 'text-red-700 dark:text-red-300'}`}>
                        {item.value === 1 ? t('admin.comments.filters.helpful', 'Helpful') : t('admin.comments.filters.notHelpful', 'Not helpful')}
                      </span>
                    </TableCell>
                    <TableCell className="py-4">
                      <button
                        type="button"
                        onClick={() => setSelectedFeedback(item)}
                        className="max-w-[440px] truncate text-left text-sm hover:underline"
                        title={item.comment || undefined}
                      >
                        {item.comment || <span className="text-muted-foreground">{t('admin.comments.allFeedback.noComment', 'No comment')}</span>}
                      </button>
                    </TableCell>
                    <TableCell className="max-w-[220px] truncate py-4 text-sm" title={item.userIdentifier || undefined}>
                      {item.userIdentifier || t('admin.comments.allFeedback.unknown', 'Unknown')}
                    </TableCell>
                    <TableCell className="max-w-[240px] truncate py-4 text-sm text-muted-foreground" title={item.threadName || undefined}>
                      {item.threadName || '—'}
                    </TableCell>
                    <TableCell className="whitespace-nowrap py-4 text-sm text-muted-foreground">{formatDate(item.createdAt)}</TableCell>
                    <TableCell className="py-4 text-right">
                      <Button
                        size="icon"
                        variant="outline"
                        className="h-8 w-8"
                        onClick={() => setSelectedFeedback(item)}
                        aria-label={t('admin.comments.allFeedback.detailsButton', 'View details')}
                        title={t('admin.comments.allFeedback.detailsButton', 'View details')}
                      >
                        <MessageSquareText className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </div>

        {totalFeedback > 0 ? (
          <div className="flex flex-wrap items-center justify-between gap-3 border-t border-border px-5 py-3">
            <p className="text-sm text-muted-foreground">
              {t('admin.comments.pagination.range', { first: firstShown, last: lastShown, total: totalFeedback, defaultValue: `${firstShown}–${lastShown} of ${totalFeedback}` })}
            </p>
            <div className="flex items-center gap-2">
              <Select value={String(pageSize)} onValueChange={(value) => { setPageSize(Number(value)); setCurrentPage(1); }}>
                <SelectTrigger className="h-8 w-[76px]" aria-label={t('admin.comments.pagination.pageSize', 'Rows per page')}><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="20">20</SelectItem>
                  <SelectItem value="50">50</SelectItem>
                  <SelectItem value="100">100</SelectItem>
                </SelectContent>
              </Select>
              <span className="min-w-20 text-center text-sm text-muted-foreground">{currentPage} / {totalPages}</span>
              <Button size="icon" variant="outline" className="h-8 w-8" onClick={() => setCurrentPage((page) => Math.max(1, page - 1))} disabled={currentPage === 1} aria-label={t('admin.comments.pagination.previous', 'Previous')}>
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <Button size="icon" variant="outline" className="h-8 w-8" onClick={() => setCurrentPage((page) => Math.min(totalPages, page + 1))} disabled={currentPage >= totalPages} aria-label={t('admin.comments.pagination.next', 'Next')}>
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        ) : null}
      </section>

      <Dialog open={Boolean(selectedFeedback)} onOpenChange={(open) => !open && setSelectedFeedback(null)}>
        <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle>{t('admin.comments.feedbackDetails.title', 'Feedback details')}</DialogTitle>
            <DialogDescription>{selectedFeedback ? formatDate(selectedFeedback.createdAt) : ''}</DialogDescription>
          </DialogHeader>

          {selectedFeedback ? (
            <div className="divide-y divide-border border-y border-border">
              <div className="grid gap-4 py-4 sm:grid-cols-[150px_1fr]">
                <p className="text-sm text-muted-foreground">{t('admin.comments.feedbackDetails.feedback.title', 'Rating')}</p>
                <div>
                  <p className={`text-sm font-semibold ${selectedFeedback.value === 1 ? 'text-emerald-700 dark:text-emerald-300' : 'text-red-700 dark:text-red-300'}`}>
                    {selectedFeedback.value === 1 ? t('admin.comments.filters.helpful', 'Helpful') : t('admin.comments.filters.notHelpful', 'Not helpful')}
                  </p>
                  <p className="mt-1 whitespace-pre-wrap text-sm text-muted-foreground">
                    {selectedFeedback.comment || t('admin.comments.allFeedback.noComment', 'No comment')}
                  </p>
                </div>
              </div>
              <div className="grid gap-4 py-4 sm:grid-cols-[150px_1fr]">
                <p className="text-sm text-muted-foreground">{t('admin.comments.feedbackDetails.userInfo.title', 'User')}</p>
                <div className="min-w-0">
                  <p className="break-all text-sm">{selectedFeedback.userIdentifier || '—'}</p>
                  {selectedFeedback.threadName ? <p className="mt-1 text-sm text-muted-foreground">{selectedFeedback.threadName}</p> : null}
                </div>
              </div>
              {selectedFeedback.stepInput ? (
                <div className="grid gap-4 py-4 sm:grid-cols-[150px_1fr]">
                  <p className="text-sm text-muted-foreground">{t('admin.comments.feedbackDetails.context.userMessage', 'User message')}</p>
                  <p className="whitespace-pre-wrap text-sm">{displayText(selectedFeedback.stepInput)}</p>
                </div>
              ) : null}
              {selectedFeedback.stepOutput ? (
                <div className="grid gap-4 py-4 sm:grid-cols-[150px_1fr]">
                  <p className="text-sm text-muted-foreground">{t('admin.comments.feedbackDetails.context.assistantResponse', 'Assistant response')}</p>
                  <p className="max-h-64 overflow-y-auto whitespace-pre-wrap text-sm">{displayText(selectedFeedback.stepOutput)}</p>
                </div>
              ) : null}
            </div>
          ) : null}

          <DialogFooter>
            <Button variant="outline" onClick={() => setSelectedFeedback(null)}>{t('admin.comments.feedbackDetails.close', 'Close')}</Button>
            <Button onClick={() => selectedFeedback?.threadId && navigate(`/admin/chat-logs/${selectedFeedback.threadId}`)} disabled={!selectedFeedback?.threadId}>
              {t('admin.comments.feedbackDetails.thread.openButton', 'Open conversation')}
              <ArrowUpRight className="ml-2 h-4 w-4" />
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminComments;
